import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  EditAssociationData,
  EditAssociationDialogComponent,
} from '../shared-components/association-panel-components/edit-association-dialog/edit-association-dialog.component';
import { Association, CrmAssociationService } from './crm-services/crm-object-service/crm-association.service';
import { CrmObjectService } from './crm-services/crm-object-service/crm-object.service';
import { Observable, switchMap, of, map, combineLatest, shareReplay } from 'rxjs';
import { CardData } from '../tokens-and-interfaces';

export function combineAssociationsWithChanges(associations: Association[], changes: Association[]): Association[] {
  const combinedData = [...associations];
  for (const updatedAssociation of changes) {
    const updatedAssociationIndex = combinedData.findIndex((a) => a.associationId === updatedAssociation.associationId);
    if (updatedAssociationIndex > -1) {
      combinedData[updatedAssociationIndex] = updatedAssociation;
    }
  }
  return [...combinedData];
}

@Injectable()
export class DefaultAssociationActionsService {
  constructor(
    private readonly dialog: MatDialog,
    private readonly associationService: CrmAssociationService,
    private readonly objectService: CrmObjectService,
  ) {}

  reloadAssociations(cardData: CardData): Observable<Association> {
    const association$ = this.associationService.getAssociation(cardData.associationId ?? '').pipe(shareReplay(1));
    const crmObject$ = association$.pipe(
      switchMap((association) => {
        switch (cardData.type) {
          case 'business':
            return this.objectService.getMultiObject('Company', [association.toId]);
          case 'user':
            return this.objectService.getMultiObject('Contact', [association.fromId]);
          default:
            return of({ crmObjects: [] });
        }
      }),
      map((response) => response?.crmObjects?.length > 0 && response.crmObjects[0]),
    );

    return combineLatest([association$, crmObject$]).pipe(
      map(([association, crmObject]) => ({
        associationId: association.associationId,
        associationType: association.associationType,
        tags: (association?.tags || []).map((tag) => tag.tagValue),
        associatedObject: crmObject,
      })),
    );
  }

  editTagsAction(cardData: CardData): Observable<Association | null> {
    let associationName = '';
    if (cardData.type === 'business') {
      associationName = cardData.businessName ?? '';
    } else if (cardData.type === 'user') {
      associationName = cardData.userName ?? '';
    }
    const dialogRef = this.dialog.open(EditAssociationDialogComponent, {
      data: {
        associationId: cardData.associationId,
        associationName: associationName,
      } as EditAssociationData,
      width: '500px',
    });
    return dialogRef.afterClosed().pipe(
      switchMap((success) => {
        if (success) {
          // associations have to be reloaded because list endpoint does not
          // immediately return the tags
          return this.reloadAssociations(cardData);
        }
        return of(null);
      }),
    );
  }
}
